/**
 * The 2FA QR code, drawn as an SVG.
 *
 * The server sends the code as Unicode block art (SnappyMail\QRCode::__toString):
 * each character is two modules stacked vertically, one line per two rows. Shown
 * as text, whether it scans depends on the font -- block glyphs that are not
 * exactly square, or a line-height the browser rounds, open hairline gaps
 * between rows, and phone cameras read those as white modules. On a phone the
 * text also wraps or scrolls at narrow widths.
 *
 * Parsing the art back into modules and drawing rects removes the font from the
 * picture entirely, and the SVG scales cleanly to any width.
 */

/** Parses the block art into a square boolean matrix, `true` = dark. */
export function parseBlockQr(art: string): boolean[][] {
	const lines = art.replace(/\r/g, '').split('\n').filter(line => line.length > 0);
	const rows: boolean[][] = [];

	for (const line of lines) {
		const top: boolean[] = [];
		const bottom: boolean[] = [];
		for (const char of line) {
			top.push('█' === char || '▀' === char);
			bottom.push('█' === char || '▄' === char);
		}
		rows.push(top, bottom);
	}

	// An odd module count leaves a padding row on the last line (its bottom half
	// is always light). Trim to a square so the quiet zone is even on all sides.
	const size = rows[0]?.length ?? 0;
	return rows.slice(0, size);
}

export function QrCode({ art, label }: { art: string; label: string }) {
	const modules = parseBlockQr(art);
	const size = modules.length;
	if (!size) {
		return null;
	}

	// Four modules of quiet zone is what the QR spec requires, and what most
	// scanners need to find the finder patterns reliably.
	const quiet = 4;
	const box = size + quiet * 2;

	let path = '';
	modules.forEach((row, y) => {
		row.forEach((dark, x) => {
			if (dark) {
				path += `M${x + quiet} ${y + quiet}h1v1h-1z`;
			}
		});
	});

	return (
		<svg
			className="qr"
			viewBox={`0 0 ${box} ${box}`}
			role="img"
			aria-label={label}
			shapeRendering="crispEdges"
		>
			<rect width={box} height={box} fill="#FFFFFF" />
			<path d={path} fill="#000000" />
		</svg>
	);
}
